import aes from 'crypto-js/aes';
import hex from 'crypto-js/enc-hex';
import pbkdf2 from 'crypto-js/pbkdf2';
import encBase64 from 'crypto-js/enc-base64';
import fileSaver from 'file-saver';

import { parseAssessmentItem } from '../qti/parser/question/QTIAssessmentItem';
import serializeItemResult from '../qti/serializer/itemResult/ItemResultSerializer';

import assessmentReader from '../store/readHelpers/AssessmentReader';
import questionReader from '../store/readHelpers/QuestionReader';
import responseReader from '../store/readHelpers/ResponseReader';
import sessionReader from '../store/readHelpers/SessionReader';
import responseService from './ResponseService';
import utilService from './UtilService';
import questionSelectionService from './QuestionSelectionService';

const fileDeliveryService = {
    saveDelivery(fileContents, assessmentRunId = sessionReader.getAssessmentRunId()) {
        const blob = new Blob([fileContents], { type: 'application/octet-stream;charset=utf-8' });
        fileSaver.saveAs(blob, this.getFileName(assessmentRunId));
    },

    getFileBlobForLink(fileContents) {
        return `data:application/octet-stream;charset=UTF-8;base64,${window.btoa(fileContents)}`;
    },

    browserRequireBlobForLink() {
        return utilService.isSebForMac() || utilService.isSafari();
    },

    getFileName(assessmentRunId = sessionReader.getAssessmentRunId()) {
        const candidateId = sessionReader.getCandidateId();
        return `Cand_${candidateId}-Test_${assessmentRunId}.ia`;
    },

    getDecryptKey() {
        if (sessionReader.isOfflineMode()) {
            return `${sessionReader.getFileSaveId()} player20offline`;
        }
        return `${sessionReader.getLoginAttemptId()} player20`;
    },

    getEncryptedFileContent(blankDelivery, deliveryObject) {
        const deliveryObjectToUse = deliveryObject || this.getDeliveryObject(!!blankDelivery, sessionReader.getAssessmentRunId());
        return this.encryptDeliveryObject(deliveryObjectToUse);
    },

    encryptDeliveryObject(deliveryObject) {
        const encryptedDelivery = this.encryptDelivery(deliveryObject);
        const decryptKey = this.getDecryptKey();
        return `${decryptKey} ${encryptedDelivery}`;
    },

    getDeliveryObject(blankDelivery, assessmentRunId) {
        return {
            assessmentRunId,
            candidateId: sessionReader.getCandidateId(),
            deliveryMode: blankDelivery ? 'blankDelivery' : '',
            itemResults: this.generateItemResults(blankDelivery, assessmentRunId),
            startTime: sessionReader.getStartTime(),
            timeOffset: utilService.getTimezoneOffsetString(),
            timestamp: utilService.getIsoDateStamp(),
            userId: sessionReader.getUserId(),
        };
    },

    getItemResult(contentRevisionId, blankDelivery, assessmentRunId) {
        let itemResult = responseReader.getItemResultForQuestion(contentRevisionId);
        if (!itemResult || blankDelivery) {
            // in case of no response on state, generate empty response as we need to
            // include response for all questions, also the non visited ones
            itemResult = this.getEmptyItemResult(contentRevisionId, assessmentRunId);
        }

        return itemResult;
    },

    getEmptyItemResult(contentRevisionId, assessmentRunId) {
        let question = assessmentReader.getQuestion(contentRevisionId);
        if (!question.isParsed) {
            question = parseAssessmentItem(question, question.defaultLanguage, assessmentRunId);
        }

        return responseService.generateInitialItemResult(question, assessmentRunId);
    },

    generateItemResults(blankDelivery, assessmentRunId = sessionReader.getAssessmentRunId()) {
        const allQuestionIds = questionReader.getAllQuestionIdsFlat();
        const itemResultObjects = [];
        allQuestionIds.forEach((contentRevisionId) => {
            const itemResult = this.getItemResult(contentRevisionId, blankDelivery, assessmentRunId);
            if (itemResult) {
                const itemResultObject = {
                    contentRevisionId,
                    itemResult: serializeItemResult(itemResult),
                    avoidStoring: questionSelectionService.avoidStoringResponse(contentRevisionId),
                };
                itemResultObjects.push(itemResultObject);
            }
        });
        return itemResultObjects;
    },

    encryptDelivery(deliveryObject) {
        // keySize, iterationCount, iv and salt have the same hardcoded values server side
        // (StoreUploadedEncryptedDelivery)
        const deliveryAsString = JSON.stringify(deliveryObject);
        // keySize is in 4 byte blocks, therefore divide by 32
        const keySize = 128 / 32;
        const iterationCount = 2;
        const iv = hex.parse('F27D5C9927726BCEFE7510B1BDD3D137');
        const salt = hex.parse('3FF2EC019C627B945225DEBAD71A01B6985FE84C95A70EB132882F88C0A59A55');
        const passPhrase = sessionReader.getFileSaveKey();
        const key = this.generateKey(salt, passPhrase, keySize, iterationCount);
        const encrypted = aes.encrypt(
            deliveryAsString,
            key,
            { iv },
        );
        return encrypted.ciphertext.toString(encBase64);
    },

    generateKey(salt, passPhrase, keySize, iterations) {
        return pbkdf2(
            passPhrase,
            salt,
            { keySize, iterations },
        );
    },
};

export default fileDeliveryService;
